// Notifications API - Unified Email & SMS
// Centralized notification sending based on client preferences
// SMS via Twilio toll-free (transactional only), Email via SendGrid

const API_BASE = '/.netlify/functions';

// ===== NOTIFICATION PREFERENCE CATEGORIES =====
// These map to client.notificationPrefs object
export const NOTIFICATION_CATEGORIES = {
  LOGIN: 'login',                    // Login codes (always sent to requested channel)
  APPOINTMENT: 'appointment',        // Confirmation & reminders
  TEAM_STATUS: 'teamStatus',        // On the way, running late, arrived, leaving
  INVOICES: 'invoices',             // Invoice sent, payment confirmation, failed, past due
  ESTIMATES: 'estimates',           // Estimate ready for review
  PORTAL_MESSAGES: 'portalMessages' // New message in portal
};

// Default notification preferences for new clients
// NOTE: All defaults are OFF - clients must opt in
export const DEFAULT_NOTIFICATION_PREFS = {
  login: { email: true, sms: true },           // Login codes always available (user chooses at login)
  appointment: { email: false, sms: false },   // Opted out by default
  teamStatus: { email: false, sms: false },    // Opted out by default
  invoices: { email: false, sms: false },      // Opted out by default
  estimates: { email: false, sms: false },     // Opted out by default
  portalMessages: { email: false, sms: false } // Opted out by default
};

// ===== LOW-LEVEL SEND FUNCTIONS =====

/**
 * Send email via SendGrid
 */
export async function sendEmail(type, data) {
  try {
    const response = await fetch(`${API_BASE}/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, data })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    return { success: true, channel: 'email', ...result };
  } catch (error) {
    console.error('Email Error:', error);
    return { success: false, channel: 'email', error: error.message };
  }
}

/**
 * Send SMS via Twilio
 */
export async function sendSMS(type, data) {
  try {
    const response = await fetch(`${API_BASE}/send-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, data })
    });

    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      return { success: false, channel: 'sms', error: `SMS server returned invalid response (status ${response.status})` };
    }
    
    if (!response.ok) {
      return { success: false, channel: 'sms', error: result.error || result.details || `SMS failed with status ${response.status}` };
    }

    return { success: true, channel: 'sms', ...result };
  } catch (error) {
    console.error('SMS Error:', error);
    return { success: false, channel: 'sms', error: error.message || 'Network error sending SMS' };
  }
}

// ===== PREFERENCE-AWARE NOTIFICATION SENDER =====

/**
 * Send notification based on client preferences
 * @param {string} category - One of NOTIFICATION_CATEGORIES
 * @param {string} emailType - Email template type (for send-email function)
 * @param {string} smsType - SMS template type (for send-sms function)
 * @param {Object} client - Client object with email, phone, notificationPrefs
 * @param {Object} emailData - Data for email template
 * @param {Object} smsData - Data for SMS template (can differ from emailData)
 */
export async function sendNotification({
  category,
  emailType,
  smsType,
  client,
  emailData,
  smsData
}) {
  const results = { email: null, sms: null };
  
  // Get client's preferences for this category
  const prefs = client?.notificationPrefs?.[category] || DEFAULT_NOTIFICATION_PREFS[category] || {};
  
  // Check if client has completely opted out
  const hasEmail = client?.email && prefs.email;
  const hasSMS = client?.phone && prefs.sms && client?.textConsent !== false;

  if (!hasEmail && !hasSMS) {
    console.log(`Notification skipped - ${category}: client opted out or missing contact info`);
    return { skipped: true, reason: 'No enabled channels or missing contact info', results };
  }

  // For invoice-related emails, use accounting email if available
  const recipientEmail = (category === NOTIFICATION_CATEGORIES.INVOICES && client?.accountingEmail)
    ? client.accountingEmail
    : client.email;

  // Send to enabled channels in parallel
  const promises = [];

  if (hasEmail && emailType) {
    promises.push(
      sendEmail(emailType, { ...emailData, clientEmail: recipientEmail })
        .then(r => { results.email = r; })
    );
  }

  if (hasSMS && smsType) {
    promises.push(
      sendSMS(smsType, { ...smsData, phone: client.phone })
        .then(r => { results.sms = r; })
    );
  }

  await Promise.all(promises);

  return {
    skipped: false,
    results,
    sentTo: {
      email: results.email?.success || false,
      sms: results.sms?.success || false
    }
  };
}

// ===== HIGH-LEVEL NOTIFICATION FUNCTIONS =====

/**
 * Send login code - special case, sends to specific channel requested
 */
export async function sendLoginCode({ channel, email, phone, code, clientName }) {
  if (channel === 'email' && email) {
    return await sendEmail('portal_login_code', { clientEmail: email, code, clientName });
  } else if (channel === 'sms' && phone) {
    return await sendSMS('login_code', { phone, code });
  }
  return { skipped: true, reason: 'Invalid channel or missing contact' };
}

/**
 * Send appointment confirmation
 */
export async function notifyAppointmentConfirmation({ client, date, time, address }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.APPOINTMENT,
    emailType: 'appointment_confirmation',
    smsType: 'appointment_confirmation',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      date,
      time,
      address
    },
    smsData: { date, time }
  });
}

/**
 * Send appointment reminder
 */
export async function notifyAppointmentReminder({ client, date, time, address, teamNames, portalLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.APPOINTMENT,
    emailType: 'appointment_reminder',
    smsType: 'appointment_reminder',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      date,
      time,
      address,
      teamNames
    },
    smsData: { date, time, portalLink }
  });
}

/**
 * Send "Team on the way" notification
 */
export async function notifyTeamOnTheWay({ client, teamName, eta, address, portalLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.TEAM_STATUS,
    emailType: 'on_my_way',
    smsType: 'team_on_the_way',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      teamMemberName: teamName,
      estimatedArrival: eta,
      address
    },
    smsData: { teamName, eta, portalLink }
  });
}

/**
 * Send "Team running late" notification
 */
export async function notifyTeamRunningLate({ client, delay, newEta }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.TEAM_STATUS,
    emailType: null, // No email template for this yet
    smsType: 'team_running_late',
    client,
    emailData: {},
    smsData: { delay, eta: newEta }
  });
}

/**
 * Send "Team arrived" notification
 */
export async function notifyTeamArrived({ client }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.TEAM_STATUS,
    emailType: null, // No email template for this yet
    smsType: 'team_arrived',
    client,
    emailData: {},
    smsData: {}
  });
}

/**
 * Send "Team leaving / job completed" notification
 */
export async function notifyJobCompleted({ client, address, feedbackLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.TEAM_STATUS,
    emailType: 'job_completed',
    smsType: 'team_leaving',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      address,
      feedbackLink
    },
    smsData: {}
  });
}

/**
 * Send invoice notification
 */
export async function notifyInvoiceSent({ client, invoiceNumber, amount, paymentLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.INVOICES,
    emailType: 'invoice_sent',
    smsType: 'invoice_sent',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      invoiceNumber,
      amount: parseFloat(amount).toFixed(2),
      paymentLink
    },
    smsData: { invoiceNumber, amount: parseFloat(amount).toFixed(2), paymentLink }
  });
}

/**
 * Send payment confirmation
 */
export async function notifyPaymentReceived({ client, amount, receiptLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.INVOICES,
    emailType: 'payment_confirmation',
    smsType: 'payment_confirmation',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      amount: parseFloat(amount).toFixed(2),
      receiptLink
    },
    smsData: { amount: parseFloat(amount).toFixed(2) }
  });
}

/**
 * Send payment failed notification
 */
export async function notifyPaymentFailed({ client, amount, portalLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.INVOICES,
    emailType: 'payment_failed',
    smsType: 'payment_failed',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      amount: parseFloat(amount).toFixed(2)
    },
    smsData: { amount: parseFloat(amount).toFixed(2), portalLink }
  });
}

/**
 * Send payment reminder (past due)
 */
export async function notifyPaymentReminder({ client, invoiceNumber, amount, dueDate, paymentLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.INVOICES,
    emailType: 'payment_reminder',
    smsType: 'payment_reminder',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      invoiceNumber,
      amount: parseFloat(amount).toFixed(2),
      dueDate
    },
    smsData: { invoiceNumber, amount: parseFloat(amount).toFixed(2), dueDate, paymentLink }
  });
}

/**
 * Send estimate ready notification
 */
export async function notifyEstimateReady({ client, approvalLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.ESTIMATES,
    emailType: 'estimate_sent',
    smsType: 'estimate_ready',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      approvalLink
    },
    smsData: { approvalLink }
  });
}

/**
 * Send new portal message notification
 */
export async function notifyNewPortalMessage({ client, subject, portalLink }) {
  return await sendNotification({
    category: NOTIFICATION_CATEGORIES.PORTAL_MESSAGES,
    emailType: 'admin_message_reply',
    smsType: 'new_portal_message',
    client,
    emailData: {
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      subject,
      portalLink
    },
    smsData: { subject, portalLink }
  });
}

// ===== INTERNAL NOTIFICATIONS (Managers only - always email) =====

/**
 * Notify managers of an issue (damage report, etc.)
 */
export async function notifyManagers({
  type,
  jobId,
  jobAddress,
  clientName,
  teamMemberName,
  description,
  photos = []
}) {
  try {
    const photoUrls = photos.map(p => p.url).filter(Boolean);
    
    return await sendEmail('manager_alert', {
      type,
      jobId,
      jobAddress,
      clientName,
      teamMemberName,
      description,
      photoCount: photos.length,
      photoUrls: photoUrls
    });
  } catch (error) {
    console.error('Failed to send manager alert:', error);
    return { error: error.message };
  }
}

/**
 * Notify manager when estimate is approved by client
 */
export async function notifyManagerEstimateApproved({ clientName, estimateId, total }) {
  return await sendEmail('estimate_approved', {
    clientName,
    estimateId,
    total
  });
}

// ===== UTILITY: Check if client is completely opted out =====

export function isClientOptedOut(client) {
  if (!client?.notificationPrefs) return false;
  
  const prefs = client.notificationPrefs;
  
  // Check if all notification categories have both email and sms disabled
  return Object.values(NOTIFICATION_CATEGORIES).every(category => {
    const catPrefs = prefs[category];
    return !catPrefs?.email && !catPrefs?.sms;
  });
}

/**
 * Get masked contact info for login screen
 * e.g., "j***@email.com" or "(434) ***-**89"
 */
export function getMaskedContact(value, type) {
  if (!value) return null;
  
  if (type === 'email') {
    const [local, domain] = value.split('@');
    if (!domain) return value;
    const maskedLocal = local.charAt(0) + '***';
    return `${maskedLocal}@${domain}`;
  }
  
  if (type === 'phone') {
    const digits = value.replace(/\D/g, '');
    if (digits.length < 4) return value;
    const lastFour = digits.slice(-4);
    return `(***) ***-${lastFour.slice(0, 2)}**`;
  }
  
  return value;
}
